import { cn } from "../lib/cn";
import {
  formatBitrate,
  formatFps,
  formatMs,
  formatPercent,
  formatResolution,
} from "../lib/format";
import type { ParticipantConnectionStats } from "../lib/types";
import { ConnectionTypeBadge } from "./Badge";

function Value({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "warn" | "bad" | "normal";
}) {
  return (
    <span
      className={cn(
        "font-mono text-[13px] tabular-nums text-zinc-200",
        tone === "warn" && "text-amber-300",
        tone === "bad" && "text-rose-300",
      )}
    >
      {children}
    </span>
  );
}

function StatItem({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: "warn" | "bad" | "normal";
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-zinc-600">
        {label}
      </p>
      <div className="mt-0.5 truncate">
        <Value tone={tone}>{value}</Value>
      </div>
    </div>
  );
}

export function StatsPanel({
  stats,
  compact = false,
  className,
}: {
  stats: ParticipantConnectionStats | null;
  compact?: boolean;
  className?: string;
}) {
  const lossTone =
    stats?.packetLossPercent != null && stats.packetLossPercent > 2
      ? "bad"
      : stats?.packetLossPercent != null && stats.packetLossPercent > 0.5
        ? "warn"
        : "normal";

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-600">
          Качество соединения
        </p>
        <ConnectionTypeBadge transport={stats?.transport} />
      </div>
      <div className={cn("grid grid-cols-2 gap-x-4 gap-y-4")}>
        <StatItem
          label="Разрешение"
          value={formatResolution(stats?.width, stats?.height)}
        />
        <StatItem label="FPS" value={formatFps(stats?.fps)} />
        <StatItem label="Битрейт" value={formatBitrate(stats?.bitrateKbps)} />
        <StatItem label="RTT" value={formatMs(stats?.rttMs)} />
        <StatItem
          label="Потери"
          value={formatPercent(stats?.packetLossPercent)}
          tone={lossTone}
        />
        <StatItem label="Jitter" value={formatMs(stats?.jitterMs)} />
        <StatItem
          label="Дропы"
          value={
            stats?.framesDropped == null ? "—" : String(stats.framesDropped)
          }
          tone={
            stats?.framesDropped && stats.framesDropped > 0 ? "warn" : "normal"
          }
        />
        <StatItem
          label="Пакеты"
          value={
            stats?.packetsLost == null ? "—" : `${stats.packetsLost} потерь`
          }
          tone={stats?.packetsLost && stats.packetsLost > 0 ? "warn" : "normal"}
        />
      </div>
      {!stats && (
        <p className="text-[11px] text-zinc-600">
          Метрики появятся после установления WebRTC-соединения.
        </p>
      )}
    </div>
  );
}
